import { useEffect, useState } from "react";

import {
  useTransition,
  animated,
  config as springConfig,
} from "@react-spring/web";
import InfoCircle from "@uui/InfoCircle";

import { Alert, AlertProps } from "@mui/material";
import styled from "@mui/material/styles/styled";

import useTimeout from "@app/common/hooks/useTimeout";

const ICON_MAPPING = {
  info: (
    <InfoCircle fontSize="inherit" sx={{ color: "shades.secondary.500" }} />
  ),
};

interface SettingAlertProps extends AlertProps {
  show: boolean;
  height?: number;
  persist?: boolean;
  margin?: boolean;
  onHide?: () => void;
  timeout?: number;
}

const StyledAlert = styled(Alert)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: "14px",
  color: theme.palette.shades.neutral[600],
}));

const SettingAlert = ({
  show: showProp,
  height = 48,
  persist,
  onHide,
  timeout = 3000,
  severity,
  sx,
  margin = true,
  ...rest
}: SettingAlertProps) => {
  const [show, setShow] = useState(showProp);

  const handleHide = () => {
    onHide ? onHide() : setShow(false);
  };

  useEffect(() => {
    // The persist alerts use the showProp
    if (persist) {
      return;
    }

    // when onHide not passed, we will hide ourselves,
    // so ignore the show prop if it's false
    if ((onHide && show !== showProp) || showProp) {
      setShow(showProp);
    }
  }, [showProp]);

  const transitions = useTransition(show, {
    from: { opacity: 0, height: 0, minHeight: 0, marginBottom: 0 },
    enter: {
      opacity: 1,
      height: "auto",
      minHeight: height,
      marginBottom: margin ? 12 : 0,
    },
    leave: { opacity: 0, height: 0, minHeight: 0, marginBottom: 0 },
    config: springConfig.stiff,
  });

  useTimeout(handleHide, timeout, show && !persist);

  if (persist) {
    if (!showProp) {
      return null;
    }
    return (
      <StyledAlert
        iconMapping={ICON_MAPPING}
        severity={severity}
        sx={sx}
        {...rest}
      />
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {transitions((style, shouldShow) => {
        return (
          shouldShow && (
            <animated.div
              style={{ overflow: "hidden", width: "100%", ...style }}
            >
              <StyledAlert
                severity={severity}
                sx={sx}
                iconMapping={ICON_MAPPING}
                {...rest}
              />
            </animated.div>
          )
        );
      })}
    </div>
  );
};

export default SettingAlert;
